import React, { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const DataTable = ({ 
  data = [], 
  columns = [], 
  loading = false,
  maxHeight = 'max-h-96',
  showHeader = true,
  stickyHeader = true,
  className = '',
  onRowClick = null,
  emptyMessage = 'Nenhum dado encontrado',
  headerTitle = '',
  // Props opcionais para configuração customizada
  customColumnWidths = null,
  customColumnPadding = null,
  customFontSize = null
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Função para calcular largura (usa custom se fornecido, senão retorna vazio para usar className)
  const getColumnStyle = (column) => {
    if (customColumnWidths) {
      return customColumnWidths(column.key);
    }
    // Retorna vazio para respeitar className da coluna (comportamento original)
    return {};
  };

  // Função para calcular padding (usa custom se fornecido, senão usa cellClassName ou padrão)
  const getDefaultPadding = (column) => {
    if (customColumnPadding) {
      return customColumnPadding(column.key);
    }
    // Comportamento original: usa cellClassName se fornecido, senão padrão
    return column.cellClassName || 'px-3 py-2';
  };

  // Tamanho da fonte
  const fontSize = customFontSize || 'text-xs';

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  if (loading) {
    return (
      <div className={`bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] overflow-hidden ${className}`}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-[color:var(--surface-muted)] rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-4 bg-[color:var(--surface-muted)] rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] overflow-hidden ${className}`}>
      {showHeader && headerTitle && (
        <div className="px-6 py-4 border-b border-[color:var(--border)]">
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">{headerTitle}</h3>
        </div>
      )}
      
      <div className={`${maxHeight} overflow-y-auto overflow-x-hidden`}>
        <div className="w-full">
          <table 
            className="w-full table-fixed divide-y divide-[color:var(--border)]" 
            style={{ 
              tableLayout: 'fixed', 
              width: '100%'
            }}
          >
            <thead className={`bg-[color:var(--surface-muted)] ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
              <tr>
                {columns.map((column) => {
                  const columnStyle = getColumnStyle(column);
                  return (
                    <th
                      key={column.key}
                      className={`px-3 py-2 ${fontSize} font-medium text-[color:var(--text-muted)] uppercase tracking-wider ${column.className || ''}`}
                      onClick={() => column.sortable && handleSort(column.key)}
                      style={{ 
                        cursor: column.sortable ? 'pointer' : 'default',
                        ...columnStyle
                      }}
                      title={column.label}
                    >
                      <div className={`flex items-center space-x-1 ${column.className?.includes('text-right') ? 'justify-end' : ''}`}>
                        <span className={customColumnWidths ? 'break-words whitespace-normal leading-tight' : 'truncate'}>
                          {column.label}
                        </span>
                        {column.sortable && (
                          <div className="flex flex-col">
                            <ChevronUp 
                              className={`w-3 h-3 ${
                                sortConfig.key === column.key && sortConfig.direction === 'asc' 
                                  ? 'text-[color:var(--accent)]' 
                                  : 'text-[color:var(--text-muted)]'
                              }`} 
                            />
                            <ChevronDown 
                              className={`w-3 h-3 -mt-1 ${
                                sortConfig.key === column.key && sortConfig.direction === 'desc' 
                                  ? 'text-[color:var(--accent)]' 
                                  : 'text-[color:var(--text-muted)]'
                              }`} 
                            />
                          </div>
                        )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
              {sortedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-[color:var(--text-muted)]">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                sortedData.map((row, index) => (
                  <tr 
                    key={index} 
                    className={`hover:bg-[color:var(--surface-muted)] ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onRowClick && onRowClick(row, index)}
                  >
                    {columns.map((column) => {
                      const cellValue = column.render ? column.render(row[column.key], row, index) : row[column.key];
                      
                      // Função auxiliar para verificar se é um valor renderizável
                      const getDisplayValue = (value) => {
                        // Se for null ou undefined
                        if (value == null) {
                          return '';
                        }
                        
                        // Se for um elemento React válido
                        if (React.isValidElement(value)) {
                          return value;
                        }
                        
                        // Se for string ou número, retornar como está
                        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                          return value;
                        }
                        
                        // Se for array, converter para string
                        if (Array.isArray(value)) {
                          return JSON.stringify(value);
                        }
                        
                        // Se for objeto, converter para JSON string
                        if (typeof value === 'object') {
                          return JSON.stringify(value);
                        }
                        
                        // Fallback: converter para string
                        return String(value);
                      };
                      
                      const displayValue = getDisplayValue(cellValue);
                      const cellText = typeof displayValue === 'string' ? displayValue : 
                                     typeof displayValue === 'object' && displayValue?.props?.children ? 
                                     displayValue.props.children : 
                                     String(displayValue || '');
                      
                      const padding = getDefaultPadding(column);
                      const columnStyle = getColumnStyle(column);
                      
                      return (
                        <td 
                          key={column.key} 
                          className={`${padding} ${fontSize} ${column.className || ''}`}
                          style={columnStyle}
                          title={cellText}
                        >
                          <div className={column.cellClassName ? 'break-words whitespace-normal' : 'truncate'}>
                            {displayValue}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {data.length > 0 && (
        <div className="px-6 py-2 bg-[color:var(--surface-muted)] border-t border-[color:var(--border)]">
          <div className="flex items-center justify-between text-xs text-[color:var(--text-muted)]">
            <span>{data.length} registros</span>
            <span>Use a rolagem vertical para navegar pelos dados ↑ ↓</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
